import { useEffect, useMemo, useState } from "react";
import React from "react";
import { Form, Modal } from "react-bootstrap";

import DstAlert from "../../../../components/DstAlert/DstAlert";
import DstButton from "../../../../components/DstButton/DstButton";
import MultiSelectDropdown from "../../../../components/MultiSelectDropdown/MultiSelectDropdown";
import MultiSelectLiveDropdown from "../../../../components/MultiSelectLiveDropdown/MultiSelectLiveDropdown";
import Popup from "../../../../components/Popup/Popup";
import RequiredLabel from "../../../../components/RequiredLabel/RequiredLabel";
import { CohortIntensity } from "../../../../models/CohortIntensity";
import translate from "../../../../services/Translate";
import { ProjectCreationModalProps } from "./CreationModal.d";
import { createProject, getCohort, getSubjects } from "./CreationModal.function";
import { generateProjectName } from "./CreationModal.function";

const ProjectCreationModal = ({ language, setShowCreationModal, showCreationModal }: ProjectCreationModalProps) => {
  const [name, setName] = useState<string>("");
  const [description, setDescription] = useState<string>("");
  const [subjects, setSubjects] = useState<any[]>([]);
  const [selectedSubject, setSelectedSubject] = useState<number | null>(null);
  const [startDate, setStartDate] = useState<string>("");
  const [endDate, setEndDate] = useState<string>("");
  const [selectedFormat, setSelectedFormat] = useState<string>("");
  const [selectedOverseer, setSelectedOverseer] = useState<number | null>(null);
  const [selectedCohort, setSelectedCohort] = useState<number | null>(null);
  const [selectedCohortData, setSelectedCohortData] = useState<any | null>(null);
  const [selectedUsers, setSelectedUsers] = useState<number[]>([]);
  const [url, setUrl] = useState<string>("");
  const [loading, setLoading] = useState<boolean>(false);

  const [showAlert, setShowAlert] = useState<boolean>(false);
  const [alertProps, setAlertProps] = useState<any>({});

  const _formatOptions = (language: string) => [
    {
      id: CohortIntensity.continuous,
      label: translate(language, "PAGES.PROJECTS.MODAL.OPTIONS.FORMAT.CONTINUOUS"),
    },
    {
      id: CohortIntensity.bootcamp,
      label: translate(language, "PAGES.PROJECTS.MODAL.OPTIONS.FORMAT.BOOTCAMP"),
    },
    {
      id: CohortIntensity.tracks,
      label: translate(language, "PAGES.PROJECTS.MODAL.OPTIONS.FORMAT.TRACKS"),
    },
    {
      id: CohortIntensity.apprenticeship,
      label: translate(language, "PAGES.PROJECTS.MODAL.OPTIONS.FORMAT.APPRENTICESHIP"),
    },
    {
      id: CohortIntensity.other,
      label: translate(language, "PAGES.PROJECTS.MODAL.OPTIONS.FORMAT.OTHER"),
    },
  ];

  const displayAlert = (props: any) => {
    setAlertProps(props);
    setShowAlert(true);
  };

  const _handleAlertClose = () => {
    setShowAlert(false);
    _resetSelection();
  };

  const _isFormValid = () => {
    if (!name.trim()) return false;
    if (!selectedCohort) return false;
    if (!selectedFormat) return false;
    if (!selectedSubject) return false;
    if (!startDate || !endDate) return false;

    return true;
  };

  const _resetSelection = (resetSelectionMultipleCreation: boolean = false) => {
    setName("");
    setDescription("");
    setSelectedSubject(null);
    setSelectedOverseer(null);
    setSelectedUsers([]);
    setUrl("");

    if (!resetSelectionMultipleCreation) {
      setStartDate("");
      setEndDate("");
      setSelectedFormat("");
      setSelectedCohort(null);
      setSelectedCohortData(null);
    }
  };

  useEffect(() => {
    getSubjects(setSubjects, language);
  }, [language]);

  useEffect(() => {
    if (!selectedCohort) {
      return;
    }
    getCohort(selectedCohort, setSelectedCohortData, language);
  }, [selectedCohort, language]);

  const selectedSubjectData = useMemo(
    () => subjects.find((subject) => subject.id === selectedSubject),
    [subjects, selectedSubject]
  );
  useEffect(() => {
    if (!selectedCohortData || !selectedSubjectData || name.trim()) return;
    setName(generateProjectName(selectedCohortData, selectedSubjectData, language));
  }, [selectedCohortData, selectedSubjectData, language, name]);

  const _handleConfirm = () => {
    const optionalFields = {
      description: description ?? "",
      overseer_id: selectedOverseer ?? null,
      cohort_id: selectedCohort ?? null,
      url: url ?? "",
      user_ids: selectedUsers ?? [],
    };
    const payload = {
      name: name.trim(),
      subject_id: selectedSubject,
      dt_start: new Date(startDate).toISOString(),
      dt_end: new Date(endDate).toISOString(),
      format: selectedFormat,
      ...optionalFields,
    };
    createProject(
      payload,
      language,
      setLoading,
      displayAlert,
      setShowCreationModal,
      () => _resetSelection(true),
      () => setShowAlert(false),
      _resetSelection
    );
  };

  return (
    <React.Fragment>
      <Popup
        show={showCreationModal}
        onClose={() => setShowCreationModal(false)}
        modalTitle={translate(language, "PAGES.PROJECTS.MODAL.TITLE")}
        scrollable
      >
        <Form.Group className="mb-3">
          <Form.Label>
            <RequiredLabel label={translate(language, "PAGES.PROJECTS.MODAL.LABELS.COHORT")} />
          </Form.Label>
          <MultiSelectLiveDropdown
            category={["cohorts"]}
            label={
              selectedCohortData?.name || selectedCohort
                ? `${translate(language, "PAGES.PROJECTS.MODAL.PLACEHOLDERS.SELECTED_COHORT")} ${selectedCohort}`
                : translate(language, "PAGES.PROJECTS.MODAL.PLACEHOLDERS.SELECT_COHORT")
            }
            language={language}
            mockClass="rounded-0 rounded-end"
            toggleClass={`w-100 border ${!selectedCohort ? "text-muted" : ""}`}
            onConfirm={(selectedCohort: any[]) => setSelectedCohort(selectedCohort[0]?.value.id || null)}
            isSingleSelect
          />
        </Form.Group>

        <Form.Group className="mb-3">
          <Form.Label>{translate(language, "PAGES.PROJECTS.MODAL.LABELS.USERS")}</Form.Label>
          <MultiSelectLiveDropdown
            category={["users"]}
            label={
              selectedUsers.length
                ? `${translate(language, "PAGES.PROJECTS.MODAL.PLACEHOLDERS.SELECTED_USERS")} ${selectedUsers}`
                : translate(language, "PAGES.PROJECTS.MODAL.PLACEHOLDERS.SELECT_USERS")
            }
            language={language}
            onConfirm={(selectedUsers) => setSelectedUsers(selectedUsers.map((user) => user.value.id))}
            toggleClass={`w-100 border ${!selectedUsers.length ? "text-muted" : ""}`}
          />
        </Form.Group>

        <Form.Group className="mb-3">
          <Form.Label>
            <RequiredLabel label={translate(language, "PAGES.PROJECTS.MODAL.LABELS.SUBJECT")} />
          </Form.Label>
          <MultiSelectDropdown
            mock={subjects.length === 0}
            btnWidth="100%"
            label={
              subjects.find((subject) => subject.id === selectedSubject)?.name ??
              translate(language, "PAGES.PROJECTS.MODAL.PLACEHOLDERS.SELECT_SUBJECT")
            }
            items={subjects}
            onConfirm={(selectedItems) => {
              setSelectedSubject(selectedItems?.[0].id);
            }}
            badgeItemProperty="name"
            displayItem={(item) => item.name}
            language={language}
            toggleClass={`w-100 ${!selectedSubject ? "text-muted" : ""}`}
            isSingleSelect
            borderToggle
          />
        </Form.Group>

        <Form.Group className="mb-3">
          <Form.Label>
            <RequiredLabel label={translate(language, "PAGES.PROJECTS.MODAL.LABELS.NAME")} />
          </Form.Label>
          <Form.Control
            type="text"
            value={name}
            onChange={(event) => setName(event.target.value)}
            placeholder={translate(language, "PAGES.PROJECTS.MODAL.PLACEHOLDERS.NAME")}
          />
        </Form.Group>

        <Form.Group className="mb-3">
          <Form.Label>{translate(language, "GLOBAL.DESCRIPTION")}</Form.Label>
          <Form.Control
            as="textarea"
            rows={3}
            value={description}
            onChange={(event) => setDescription(event.target.value)}
            placeholder={translate(language, "PAGES.PROJECTS.MODAL.PLACEHOLDERS.DESCRIPTION")}
          />
        </Form.Group>

        <Form.Group className="mb-3">
          <Form.Label>
            <RequiredLabel label={translate(language, "GLOBAL.FORMAT")} />
          </Form.Label>
          <MultiSelectDropdown
            btnWidth="100%"
            label={
              _formatOptions(language).find((format) => format.id === selectedFormat)?.label ??
              translate(language, "PAGES.PROJECTS.MODAL.PLACEHOLDERS.SELECT_FORMAT")
            }
            items={_formatOptions(language)}
            onConfirm={(selectedItems) => {
              setSelectedFormat(selectedItems?.[0]?.id || null);
            }}
            badgeItemProperty="label"
            displayItem={(item) => item.label}
            language={language}
            toggleClass={`w-100 ${!selectedFormat ? "text-muted" : ""}`}
            isSingleSelect
            borderToggle
          />
        </Form.Group>

        <Form.Group className="mb-3">
          <Form.Label>
            <RequiredLabel label={translate(language, "GLOBAL.START_DATE")} />
          </Form.Label>
          <Form.Control
            className={`border-secondary ${!startDate ? "text-muted" : ""}`}
            type="datetime-local"
            value={startDate}
            onChange={(event) => setStartDate(event.target.value)}
          />
        </Form.Group>

        <Form.Group className="mb-3">
          <Form.Label>
            <RequiredLabel label={translate(language, "GLOBAL.END_DATE")} />
          </Form.Label>
          <Form.Control
            className={`border-secondary ${!endDate ? "text-muted" : ""}`}
            type="datetime-local"
            value={endDate}
            onChange={(event) => setEndDate(event.target.value)}
          />
        </Form.Group>

        <Form.Group className="mb-3">
          <Form.Label>{translate(language, "PAGES.PROJECTS.MODAL.LABELS.OVERSEER")}</Form.Label>
          <MultiSelectLiveDropdown
            category={["users"]}
            label={
              selectedOverseer
                ? `${translate(language, "PAGES.PROJECTS.MODAL.PLACEHOLDERS.SELECTED_OVERSEER")} ${selectedOverseer}`
                : translate(language, "PAGES.PROJECTS.MODAL.PLACEHOLDERS.SELECT_OVERSEER")
            }
            language={language}
            onConfirm={(selectedOverseer) => setSelectedOverseer(selectedOverseer[0]?.value.id || null)}
            toggleClass={`w-100 border ${!selectedOverseer ? "text-muted" : ""}`}
            isSingleSelect
          />
        </Form.Group>

        <Form.Group className="mb-3">
          <Form.Label>{translate(language, "PAGES.PROJECTS.MODAL.LABELS.PROJECT_LINK")}</Form.Label>
          <Form.Control
            className={`border-secondary ${!url ? "text-muted" : ""}`}
            type="text"
            placeholder={translate(language, "PAGES.PROJECTS.MODAL.PLACEHOLDERS.PROJECT_LINK")}
            value={url}
            onChange={(event) => setUrl(event.target.value)}
          />
        </Form.Group>

        <Modal.Footer>
          <DstButton
            variant="secondary"
            value={translate(language, "GLOBAL.CANCEL")}
            clickFunction={() => {
              setShowCreationModal(false);
              _resetSelection();
            }}
          />
          <DstButton
            value={translate(language, "GLOBAL.CREATE")}
            clickFunction={_handleConfirm}
            disabled={!_isFormValid()}
            loading={loading}
          />
        </Modal.Footer>
      </Popup>
      <DstAlert show={showAlert} onHide={_handleAlertClose} {...alertProps} />
    </React.Fragment>
  );
};

export default ProjectCreationModal;
